import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIT_METHOD_CFG = {
  LOGIN: { label: 'Giriş', cls: 'bg-green-800' },
  LOGOUT: { label: 'Çıkış', cls: 'bg-gray-700' },
  POST: { label: 'Ekleme', cls: 'bg-blue-700' },
  PUT: { label: 'Güncelleme', cls: 'bg-amber-700' },
  PATCH: { label: 'Güncelleme', cls: 'bg-amber-700' },
  DELETE: { label: 'Silme', cls: 'bg-red-700' },
  EXPORT: { label: 'Yedek İndirme', cls: 'bg-purple-700' },
};

const auditModuleLabel = (path) => {
  if (!path) return '—';
  const p = path.replace('/api/', '');
  const map = [
    ['export', 'Veritabanı Yedek'], ['pk/', 'Periyodik Kontrol'], ['isy/', 'ISY'],
    ['ihale', 'İhaleler'], ['odenek', 'Ödenek'], ['hakedis', 'Hakediş'],
    ['bakim-onarim/firma', 'Firma Bilgileri'], ['bakim-onarim/dogrudan', 'Doğrudan Temin'], ['bakim-onarim', 'Bakım-Onarım'],
    ['emlak', 'Emlak'], ['kamulastirma', 'Kamulaştırma'], ['tasks', 'Görevler'], ['pending-tasks', 'Bekleyen Görevler'],
    ['users', 'Kullanıcı Yönetimi'], ['sms', 'SMS Ayarları'], ['mail', 'Mail Ayarları'],
    ['settings/session-config', 'Oturum Ayarları'], ['admin/sessions', 'Oturum Yönetimi'],
    ['santiye', 'Şantiye'], ['notes', 'Notlar'], ['finajan', 'FinAjan'], ['settings', 'Ayarlar'],
    ['auth', 'Oturum'], ['archive', 'Arşiv'],
  ];
  for (const [k, v] of map) { if (p.startsWith(k) || p.includes(k)) return v; }
  return p.split('/')[0];
};

export const DenetimKaydiPanel = ({ selfOnly = false }) => {
  const [logs, setLogs] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ f_user_id: '', f_method: '', search: '' });
  const [users, setUsers] = useState([]);

  const load = useCallback(async (page = 1, f = filters) => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (!selfOnly && f.f_user_id) params.f_user_id = f.f_user_id;
      if (f.f_method) params.f_method = f.f_method;
      if (f.search) params.search = f.search;
      const endpoint = selfOnly ? `${API}/my-audit-logs` : `${API}/admin/audit-logs`;
      const res = await axios.get(endpoint, { params, withCredentials: true });
      setLogs(res.data);
    } catch (e) { /* panel mesajı yerine sessiz */ }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, [filters, selfOnly]);

  useEffect(() => {
    if (!selfOnly) {
      axios.get(`${API}/users-simple`, { withCredentials: true }).then(res => setUsers(res.data || [])).catch(() => {});
    }
    load(1, { f_user_id: '', f_method: '', search: '' });
    // eslint-disable-next-line
  }, []);

  const colCount = selfOnly ? 6 : 7;

  return (
    <Card className="bg-gray-900 border-gray-800" data-testid={selfOnly ? 'islem-gecmisim-panel' : 'denetim-kaydi-panel'}>
      <CardHeader>
        <CardTitle className="text-white text-lg">{selfOnly ? `🕘 İşlem Geçmişim (${logs.total})` : `🛡 Denetim Kaydı (${logs.total})`}</CardTitle>
        <CardDescription className="text-gray-500 text-xs">
          {selfOnly
            ? 'Yalnızca sizin yaptığınız işlemler: giriş/çıkış, ekleme, güncelleme ve silme. Kayıtlar 90 gün saklanır.'
            : 'Kim ne zaman ne yaptı: giriş/çıkış, ekleme, güncelleme, silme ve yedek indirme işlemleri. Kayıtlar 90 gün saklanır.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {!selfOnly && (
          <Select value={filters.f_user_id || '_all'} onValueChange={v => { const f = { ...filters, f_user_id: v === '_all' ? '' : v }; setFilters(f); load(1, f); }}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white w-[190px] h-9 text-sm" data-testid="audit-user-filter"><SelectValue placeholder="Kullanıcı" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-[260px]">
              <SelectItem value="_all">Tüm Kullanıcılar</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.username}</SelectItem>)}
            </SelectContent>
          </Select>
          )}
          <Select value={filters.f_method || '_all'} onValueChange={v => { const f = { ...filters, f_method: v === '_all' ? '' : v }; setFilters(f); load(1, f); }}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white w-[170px] h-9 text-sm" data-testid="audit-method-filter"><SelectValue placeholder="İşlem Türü" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white">
              <SelectItem value="_all">Tüm İşlemler</SelectItem>
              <SelectItem value="LOGIN">Giriş</SelectItem>
              <SelectItem value="LOGOUT">Çıkış</SelectItem>
              <SelectItem value="POST">Ekleme</SelectItem>
              <SelectItem value="PUT">Güncelleme</SelectItem>
              <SelectItem value="DELETE">Silme</SelectItem>
              <SelectItem value="EXPORT">Yedek İndirme</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
            onKeyDown={e => { if (e.key === 'Enter') load(1); }}
            placeholder={selfOnly ? 'İşlem yolu ara (Enter)' : 'Kullanıcı veya yol ara (Enter)'}
            className="bg-gray-800 border-gray-700 text-white h-9 w-[220px] text-sm"
            data-testid="audit-search-input"
          />
          <Button onClick={() => load(logs.page)} variant="outline" className="border-gray-700 bg-transparent text-gray-300 hover:bg-gray-800 h-9" data-testid="audit-refresh-btn">Yenile</Button>
        </div>
        {loading ? (
          <div className="text-gray-400 text-center py-8">Yükleniyor...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-log-table">
              <thead>
                <tr className="border-b border-gray-700 text-xs text-gray-400 bg-gray-800/50">
                  <th className="text-left py-2 px-3">Zaman</th>
                  {!selfOnly && <th className="text-left py-2 px-3">Kullanıcı</th>}
                  <th className="text-center py-2 px-3">İşlem</th>
                  <th className="text-left py-2 px-3">Modül</th>
                  <th className="text-left py-2 px-3">Yol</th>
                  <th className="text-left py-2 px-3">IP</th>
                  <th className="text-center py-2 px-3">Sonuç</th>
                </tr>
              </thead>
              <tbody>
                {logs.items.length === 0 ? (
                  <tr><td colSpan={colCount} className="text-center py-6 text-gray-500">Kayıt bulunamadı</td></tr>
                ) : logs.items.map((log, i) => {
                  const cfg = AUDIT_METHOD_CFG[log.method] || { label: log.method, cls: 'bg-gray-700' };
                  const ok = log.status_code < 400;
                  return (
                    <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                      <td className="py-1.5 px-3 text-gray-400 text-xs whitespace-nowrap">{log.timestamp ? new Date(log.timestamp).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}</td>
                      {!selfOnly && <td className="py-1.5 px-3 text-white text-xs">{log.name || log.username}</td>}
                      <td className="py-1.5 px-3 text-center"><Badge className={`${cfg.cls} text-white text-[10px]`}>{cfg.label}</Badge></td>
                      <td className="py-1.5 px-3 text-gray-300 text-xs">{auditModuleLabel(log.path)}</td>
                      <td className="py-1.5 px-3 text-gray-500 text-[11px] font-mono max-w-[240px] truncate" title={log.path}>{log.path}</td>
                      <td className="py-1.5 px-3 text-gray-400 text-xs">{log.ip || '—'}</td>
                      <td className="py-1.5 px-3 text-center">
                        <span className={`text-[10px] font-semibold ${ok ? 'text-green-400' : 'text-red-400'}`}>{ok ? 'Başarılı' : `Reddedildi (${log.status_code})`}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-3">
          <span className="text-xs text-gray-500">Sayfa {logs.page} / {logs.total_pages} · Toplam {logs.total} kayıt</span>
          <div className="flex gap-2">
            <Button disabled={logs.page <= 1 || loading} onClick={() => load(logs.page - 1)} variant="outline" className="border-gray-700 bg-transparent text-gray-300 hover:bg-gray-800 h-8" data-testid="audit-prev-btn">Önceki</Button>
            <Button disabled={logs.page >= logs.total_pages || loading} onClick={() => load(logs.page + 1)} variant="outline" className="border-gray-700 bg-transparent text-gray-300 hover:bg-gray-800 h-8" data-testid="audit-next-btn">Sonraki</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
